import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { ChevronDown, Search, HelpCircle } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import FAQSchema from '../components/FAQSchema';
import BreadcrumbSchema from '../components/BreadcrumbSchema';

const API = `${window.location.origin}/api`;

const CATEGORY_LABELS = {
  general: 'General',
  fragrance: 'Fragrance & Wear',
  product: 'Products & Selection',
  shipping: 'Shipping',
  orders: 'Orders & Payment',
  returns: 'Returns & Authenticity',
  ambassador: 'Ambassador Program',
};

export default function FAQPage() {
  const [faqs, setFaqs] = useState([]);
  const [open, setOpen] = useState({});
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState('all');

  useEffect(() => {
    axios.get(`${API}/faqs`).then(r => setFaqs(r.data || [])).catch(() => {});
  }, []);

  const grouped = useMemo(() => {
    const filtered = faqs.filter(f => {
      const matchCat = activeCat === 'all' || f.category === activeCat;
      const matchSearch = !search || f.question.toLowerCase().includes(search.toLowerCase()) || f.answer.toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    });
    const out = {};
    for (const f of filtered) {
      out[f.category] = out[f.category] || [];
      out[f.category].push(f);
    }
    return out;
  }, [faqs, activeCat, search]);

  const allCats = useMemo(() => ['all', ...Object.keys(CATEGORY_LABELS).filter(k => faqs.some(f => f.category === k))], [faqs]);
  const totalShown = Object.values(grouped).reduce((s, arr) => s + arr.length, 0);

  return (
    <main className="min-h-screen bg-[#0A0A0A]">
      <SEOHead
        title="Frequently Asked Questions"
        description="Answers to the most-searched questions about JS Fragrance — fragrance care, shipping, returns, payment options, and more. EDP at 30% oil concentration (Extrait-level), hand-blended from the Philippines."
        url="https://jsfragrances.com/faq"
      />
      <FAQSchema faqs={faqs} />
      <BreadcrumbSchema crumbs={[
        { name: 'Home', path: '/' },
        { name: 'FAQ', path: '/faq' },
      ]} />

      <header className="max-w-4xl mx-auto px-4 sm:px-6 pt-12 pb-8 text-center">
        <HelpCircle className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" strokeWidth={1.4} />
        <h1 className="text-4xl sm:text-5xl font-light text-white mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Frequently Asked Questions
        </h1>
        <p className="text-white/50 text-sm font-sans max-w-xl mx-auto">
          Everything you might be wondering about JS Fragrance — products, shipping, returns, fragrance care.
          Can&apos;t find your answer? Message us anytime.
        </p>
      </header>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-20">
        {/* Search */}
        <div className="mb-6 relative">
          <Search className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search questions…"
            className="w-full bg-[#141414] border border-white/10 pl-10 pr-3 py-2.5 text-sm text-white font-sans focus:border-[#D4AF37] focus:outline-none"
            data-testid="faq-search"
          />
        </div>

        {/* Category filter */}
        <nav className="flex flex-wrap gap-2 mb-8" aria-label="FAQ categories">
          {allCats.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCat(cat)}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-sans border transition-colors ${
                activeCat === cat
                  ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5'
                  : 'border-white/10 text-white/50 hover:border-white/30 hover:text-white/80'
              }`}
              data-testid={`faq-cat-${cat}`}
            >
              {cat === 'all' ? 'All' : CATEGORY_LABELS[cat] || cat}
            </button>
          ))}
        </nav>

        {totalShown === 0 ? (
          <div className="text-center py-16">
            <p className="text-white/40 text-sm font-sans">No questions match your search.</p>
          </div>
        ) : (
          Object.entries(grouped).map(([catKey, catFaqs]) => (
            <section key={catKey} className="mb-10" aria-labelledby={`faq-h-${catKey}`}>
              <h2 id={`faq-h-${catKey}`} className="text-lg uppercase tracking-[0.22em] text-[#D4AF37] font-sans font-medium mb-4">
                {CATEGORY_LABELS[catKey] || catKey}
              </h2>
              <div className="divide-y divide-white/5 border-y border-white/5">
                {catFaqs.map(f => {
                  const isOpen = !!open[f.id];
                  return (
                    <article key={f.id} className="py-4">
                      <button
                        onClick={() => setOpen(o => ({ ...o, [f.id]: !o[f.id] }))}
                        className="w-full flex items-start justify-between gap-4 text-left group"
                        aria-expanded={isOpen}
                        data-testid={`faq-q-${f.id}`}
                      >
                        <h3 className="text-white text-base sm:text-lg font-sans font-medium group-hover:text-[#D4AF37] transition-colors">
                          {f.question}
                        </h3>
                        <ChevronDown
                          className={`w-4 h-4 text-white/40 mt-1.5 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        />
                      </button>
                      {isOpen && (
                        <p className="text-white/70 text-sm font-sans mt-3 leading-relaxed whitespace-pre-line">
                          {f.answer}
                        </p>
                      )}
                    </article>
                  );
                })}
              </div>
            </section>
          ))
        )}

        <div className="mt-12 p-6 border border-white/10 bg-white/[0.02] text-center">
          <p className="text-white/60 text-sm font-sans mb-3">Still have questions?</p>
          <a href="/about#contact" className="inline-block px-6 py-2.5 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.22em] font-sans font-semibold hover:bg-[#F3E5AB] transition-colors">
            Contact Us
          </a>
        </div>
      </section>
    </main>
  );
}
